import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Tabs from '../components/Tabs';
import ProjectCard from '../components/ProjectCard';
import RevolvingGlobe from '../components/RevolvingGlobe';
import { useCategories, useProjects } from '../hooks/useApi';
import { Project } from '../types';

const Home: React.FC = () => {
  const { categories, loading: categoriesLoading } = useCategories();
  const { projects, loading: projectsLoading } = useProjects();
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(categoriesLoading || projectsLoading);
  }, [categoriesLoading, projectsLoading]);

  useEffect(() => {
    if (activeCategory === null) {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(project => project.category_id === activeCategory));
    }
  }, [activeCategory, projects]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-black to-deep-navy py-8 px-4 sm:px-6 lg:px-8">
      {/* Navigation Bar */}
      <nav className="max-w-[95%] xl:max-w-[85%] mx-auto flex justify-between items-center mb-12">
        <div className="text-xl font-bold text-accent-blue">Procista AI</div>
        <div className="flex space-x-6">
          <Link 
            to="/" 
            className="text-medium-gray hover:text-accent-blue transition-colors duration-300 font-medium"
          >
            Home
          </Link>
          <Link 
            to="/about" 
            className="text-medium-gray hover:text-accent-blue transition-colors duration-300 font-medium"
          >
            About Us
          </Link>
          <button 
            onClick={() => window.open('https://calendly.com/waceem/initial-project-consultation', '_blank')}
            className="bg-gradient-to-r from-navy-blue to-accent-blue hover:from-accent-blue hover:to-navy-blue text-white font-semibold py-2 px-6 rounded-full transition-all duration-300 text-sm"
          >
            Contact
          </button>
        </div>
      </nav>

      <div className="max-w-[95%] xl:max-w-[85%] mx-auto">
        {/* Header */}
        <header className="text-center mb-16 pt-10">
          <h1 className="text-6xl md:text-8xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-accent-blue via-blue-400 to-navy-blue mb-6">
            Procista AI – Building Intelligent Business Solutions
          </h1>
          <p className="text-xl text-medium-gray max-w-4xl mx-auto">
            Transform your business with our cutting-edge AI solutions designed to optimize operations, increase efficiency, and drive growth.
          </p>
        </header>

        {/* Tabs Section */}
        <section className="mb-16">
          <Tabs 
            categories={categories} 
            activeCategory={activeCategory} 
            setActiveCategory={setActiveCategory} 
          />
        </section>

        {/* Projects Section */}
        <section>
          <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center">
            {activeCategory 
              ? `Projects in ${categories.find(c => c.id === activeCategory)?.name}` 
              : 'Featured Projects'}
          </h2>
          
          {filteredProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {filteredProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-2xl text-medium-gray">No projects found in this category.</p>
            </div>
          )}
        </section>

        {/* CTA Section */}
        <section className="mt-24 py-16 text-center bg-gradient-to-r from-navy-blue/40 to-deep-navy/40 rounded-2xl border border-deep-navy">
          <h2 className="text-4xl font-bold mb-6">Ready to transform your business?</h2>
          <p className="text-xl text-light max-w-3xl mx-auto mb-10">
            Book a free consultation with our experts to discover how our AI solutions can revolutionize your operations.
          </p>
          <button 
            onClick={() => window.open('https://calendly.com/waceem/initial-project-consultation', '_blank')}
            className="bg-gradient-to-r from-navy-blue to-accent-blue hover:from-accent-blue hover:to-navy-blue text-white font-semibold py-4 px-10 rounded-full transition-all duration-300 transform hover:scale-105 text-lg"
          >
            Schedule Free Demo
          </button>
        </section>
      </div>
      
      {/* Revolving Globe */}
      <RevolvingGlobe />
    </div>
  );
};

export default Home;